<template>
  <div class="admin-dashboard">
    <!-- Navigation -->
    <main-navigation></main-navigation>
    
    <!-- Dashboard Header -->
    <div class="dashboard-header bg-primary text-white py-4">
      <div class="container">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">Bursary Administration Dashboard</h1>
            <p class="mb-0 opacity-75">Monitor applications, track approvals, and manage bursary distribution</p>
          </div>
          <div class="text-end">
            <div class="fs-5">{{ currentDate }}</div>
            <small class="opacity-75">Last updated: {{ lastUpdated }}</small>
            <button @click="refreshData" class="btn btn-sm btn-light ms-2">
              <i class="bi bi-arrow-clockwise"></i> Refresh
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Dashboard Content -->
    <div class="container py-4">
      <!-- Category Tabs -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-body p-0">
              <div class="d-flex justify-content-between align-items-center p-3">
                <div class="btn-group" role="group">
                  <button @click="activeTab = 'all'" 
                    :class="['btn', activeTab === 'all' ? 'btn-primary' : 'btn-outline-primary']">
                    <i class="bi bi-view-list me-2"></i> All Applications
                  </button>
                  <button @click="activeTab = 'secondary'" 
                    :class="['btn', activeTab === 'secondary' ? 'btn-primary' : 'btn-outline-primary']">
                    <i class="bi bi-mortarboard me-2"></i> Secondary ({{ secondaryStudents.length }})
                  </button>
                  <button @click="activeTab = 'college'" 
                    :class="['btn', activeTab === 'college' ? 'btn-primary' : 'btn-outline-primary']">
                    <i class="bi bi-building me-2"></i> College ({{ collegeStudents.length }})
                  </button>
                </div>
                <div class="text-muted">
                  Showing: {{ getActiveTabCount }} applications
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats Cards -->
      <div class="row g-4 mb-4">
        <!-- Total Applications -->
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Total Applications</h6>
                  <h2 class="mb-0 fw-bold text-primary">{{ totalStudents }}</h2>
                </div>
                <div class="stat-icon bg-primary bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-people-fill text-primary fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <div class="row g-1">
                  <div class="col-6">
                    <small class="text-muted d-block">Secondary</small>
                    <div class="fw-bold text-primary">{{ secondaryStudents.length }}</div>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">College</small>
                    <div class="fw-bold text-success">{{ collegeStudents.length }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Approved Applications -->
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Approved</h6>
                  <h2 class="mb-0 fw-bold text-success">{{ approvedStudents.length }}</h2>
                </div>
                <div class="stat-icon bg-success bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-check-circle-fill text-success fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <div class="row g-1">
                  <div class="col-6">
                    <small class="text-muted d-block">Secondary</small>
                    <div class="fw-bold">{{ approvedSecondaryStudents.length }}</div>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">College</small>
                    <div class="fw-bold">{{ approvedCollegeStudents.length }}</div>
                  </div>
                </div>
                <small class="text-muted d-block mt-2">
                  {{ approvedPercentage }}% approval rate
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Total Awarded -->
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Total Awarded</h6>
                  <h2 class="mb-0 fw-bold text-info">{{ totalAmountAwarded.toLocaleString() }} KSh</h2>
                </div>
                <div class="stat-icon bg-info bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-cash-stack text-info fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <div class="row g-1">
                  <div class="col-6">
                    <small class="text-muted d-block">Secondary</small>
                    <div class="fw-bold">{{ totalSecondaryAwarded.toLocaleString() }} KSh</div>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">College</small>
                    <div class="fw-bold">{{ totalCollegeAwarded.toLocaleString() }} KSh</div>
                  </div>
                </div>
                <small class="text-muted d-block mt-2">
                  {{ averageAward.toLocaleString() }} KSh average
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Processing Status -->
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Processing Status</h6>
                  <div class="d-flex align-items-baseline">
                    <h2 class="mb-0 fw-bold">{{ pendingStudents.length }}</h2>
                    <small class="ms-2 text-muted">pending</small>
                  </div>
                </div>
                <div class="stat-icon bg-warning bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-clock-fill text-warning fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar bg-success" :style="{ width: approvedPercentage + '%' }"></div>
                  <div class="progress-bar bg-danger" :style="{ width: rejectedPercentage + '%' }"></div>
                  <div class="progress-bar bg-warning" :style="{ width: pendingPercentage + '%' }"></div>
                </div>
                <div class="d-flex justify-content-between mt-2">
                  <small class="text-success">{{ approvedPercentage }}% ✓</small>
                  <small class="text-danger">{{ rejectedPercentage }}% ✗</small>
                  <small class="text-warning">{{ pendingPercentage }}% ⏳</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Dashboard Grid -->
      <div class="row g-4">
        <!-- Left Column: Detailed Statistics -->
        <div class="col-lg-8">
          <!-- Category Comparison -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-bar-chart-fill me-2 text-primary"></i>
                Category Comparison
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="p-3 border rounded">
                    <h6 class="fw-bold text-primary mb-3">
                      <i class="bi bi-mortarboard me-2"></i> Secondary Schools
                    </h6>
                    <div class="row g-2 mb-3">
                      <div class="col-6">
                        <div class="text-center p-2 bg-primary bg-opacity-10 rounded">
                          <small class="text-muted d-block">Total</small>
                          <div class="h4 fw-bold text-primary">{{ secondaryStudents.length }}</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-center p-2 bg-success bg-opacity-10 rounded">
                          <small class="text-muted d-block">Approved</small>
                          <div class="h4 fw-bold text-success">{{ approvedSecondaryStudents.length }}</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-center p-2 bg-danger bg-opacity-10 rounded">
                          <small class="text-muted d-block">Rejected</small>
                          <div class="h4 fw-bold text-danger">{{ rejectedSecondaryStudents.length }}</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-center p-2 bg-warning bg-opacity-10 rounded">
                          <small class="text-muted d-block">Pending</small>
                          <div class="h4 fw-bold text-warning">{{ pendingSecondaryStudents.length }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="text-center">
                      <small class="text-muted">
                        {{ secondaryApprovalPercentage }}% approval rate • 
                        {{ totalSecondaryAwarded.toLocaleString() }} KSh awarded
                      </small>
                    </div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="p-3 border rounded">
                    <h6 class="fw-bold text-success mb-3">
                      <i class="bi bi-building me-2"></i> College & University
                    </h6>
                    <div class="row g-2 mb-3">
                      <div class="col-6">
                        <div class="text-center p-2 bg-success bg-opacity-10 rounded">
                          <small class="text-muted d-block">Total</small>
                          <div class="h4 fw-bold text-success">{{ collegeStudents.length }}</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-center p-2 bg-success bg-opacity-10 rounded">
                          <small class="text-muted d-block">Approved</small>
                          <div class="h4 fw-bold text-success">{{ approvedCollegeStudents.length }}</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-center p-2 bg-danger bg-opacity-10 rounded">
                          <small class="text-muted d-block">Rejected</small>
                          <div class="h4 fw-bold text-danger">{{ rejectedCollegeStudents.length }}</div>
                        </div>
                      </div>
                      <div class="col-6">
                        <div class="text-center p-2 bg-warning bg-opacity-10 rounded">
                          <small class="text-muted d-block">Pending</small>
                          <div class="h4 fw-bold text-warning">{{ pendingCollegeStudents.length }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="text-center">
                      <small class="text-muted">
                        {{ collegeApprovalPercentage }}% approval rate • 
                        {{ totalCollegeAwarded.toLocaleString() }} KSh awarded
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Remarks Analysis -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0 fw-bold">
                  <i class="bi bi-chat-text-fill me-2 text-info"></i>
                  Remarks Analysis
                </h5>
                <div class="text-muted">
                  <span class="badge bg-primary">{{ remarksApproved.length }} Approved</span>
                  <span class="badge bg-warning ms-2">{{ remarksFormMissing.length }} Missing Forms</span>
                  <span class="badge bg-danger ms-2">{{ remarksVoterMissing.length }} Missing Voter</span>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div class="row g-3 mb-4">
                <div class="col-md-4">
                  <div class="p-3 border border-success border-2 rounded">
                    <h6 class="fw-bold text-success mb-2">
                      <i class="bi bi-check-circle me-2"></i> Student Approved
                    </h6>
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="h3 mb-0 fw-bold">{{ remarksApproved.length }}</div>
                        <small class="text-muted">Total remarks</small>
                      </div>
                      <div class="text-end">
                        <div class="fw-bold">{{ remarksApprovedSecondary.length }}</div>
                        <small class="text-muted">Secondary</small>
                      </div>
                      <div class="text-end">
                        <div class="fw-bold">{{ remarksApprovedCollege.length }}</div>
                        <small class="text-muted">College</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="p-3 border border-warning border-2 rounded">
                    <h6 class="fw-bold text-warning mb-2">
                      <i class="bi bi-file-earmark-excel me-2"></i> Form Missing
                    </h6>
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="h3 mb-0 fw-bold">{{ remarksFormMissing.length }}</div>
                        <small class="text-muted">Need documentation</small>
                      </div>
                      <div class="text-end">
                        <div class="fw-bold">{{ remarksFormMissingSecondary.length }}</div>
                        <small class="text-muted">Secondary</small>
                      </div>
                      <div class="text-end">
                        <div class="fw-bold">{{ remarksFormMissingCollege.length }}</div>
                        <small class="text-muted">College</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="p-3 border border-danger border-2 rounded">
                    <h6 class="fw-bold text-danger mb-2">
                      <i class="bi bi-person-x me-2"></i> Voter Details Missing
                    </h6>
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="h3 mb-0 fw-bold">{{ remarksVoterMissing.length }}</div>
                        <small class="text-muted">Need verification</small>
                      </div>
                      <div class="text-end">
                        <div class="fw-bold">{{ remarksVoterMissingSecondary.length }}</div>
                        <small class="text-muted">Secondary</small>
                      </div>
                      <div class="text-end">
                        <div class="fw-bold">{{ remarksVoterMissingCollege.length }}</div>
                        <small class="text-muted">College</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Quick Actions & Processing -->
        <div class="col-lg-4">
          <!-- Quick Actions Panel -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-lightning-fill me-2 text-warning"></i>
                Quick Actions
              </h5>
            </div>
            <div class="card-body">
              <div class="list-group list-group-flush">
                <router-link to="/students/all" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-view-list text-primary"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">View All Applications</h6>
                      <small class="text-muted">{{ totalStudents }} total applications</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
                
                <router-link to="/students/secondary" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-mortarboard text-primary"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">Secondary School Students</h6>
                      <small class="text-muted">{{ secondaryStudents.length }} applications</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
                
                <router-link to="/students/college" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-building text-success"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">College & University</h6>
                      <small class="text-muted">{{ collegeStudents.length }} applications</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
                
                <router-link to="/students/remarks" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-info bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-chat-left-text-fill text-info"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">Review Remarks</h6>
                      <small class="text-muted">{{ totalRemarks }} applications with remarks</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Ward Distribution -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-geo-alt-fill me-2 text-primary"></i>
                Ward Distribution
              </h5>
            </div>
            <div class="card-body">
              <div class="mb-3" v-for="ward in topWards" :key="ward.name">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div>
                    <small class="fw-bold">{{ ward.name }}</small>
                  </div>
                  <div class="text-end">
                    <span class="badge bg-primary rounded-pill">{{ ward.secondary }}</span>
                    <span class="badge bg-success rounded-pill ms-1">{{ ward.college }}</span>
                  </div>
                </div>
                <div class="progress" style="height: 6px;">
                  <div class="progress-bar bg-primary" :style="{ width: (ward.secondary/ward.total)*100 + '%' }"></div>
                  <div class="progress-bar bg-success" :style="{ width: (ward.college/ward.total)*100 + '%' }"></div>
                </div>
                <small class="text-muted d-block mt-1">
                  {{ ward.total }} total • {{ ward.approved }} approved • {{ ward.awarded.toLocaleString() }} KSh
                </small>
              </div>
            </div>
          </div>

          <!-- Voter Verification -->
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-person-check-fill me-2 text-purple"></i>
                Voter Verification
              </h5>
            </div>
            <div class="card-body">
              <div class="text-center mb-3">
                <div class="display-6 fw-bold text-purple">{{ voterVerifiedStudents.length }}</div>
                <small class="text-muted">Verified through voter details</small>
              </div>
              
              <div class="row g-2 mb-3">
                <div class="col-6">
                  <div class="text-center p-2 bg-purple bg-opacity-10 rounded">
                    <small class="text-muted d-block">Secondary</small>
                    <div class="fw-bold">{{ voterVerifiedSecondary.length }}</div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="text-center p-2 bg-purple bg-opacity-10 rounded">
                    <small class="text-muted d-block">College</small>
                    <div class="fw-bold">{{ voterVerifiedCollege.length }}</div>
                  </div>
                </div>
              </div>
              
              <div class="d-grid">
                <button @click="viewVoterVerified" class="btn btn-outline-purple btn-sm">
                  <i class="bi bi-eye me-2"></i> View Verified Applications
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminDashboard',
  data() {
    return {
      currentDate: new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      lastUpdated: 'Just now',
      activeTab: 'all',
      secondaryStudents: [],
      collegeStudents: [],
      loading: false
    };
  },
  computed: {
    // Combined data
    allStudents() {
      return [
        ...this.secondaryStudents.map(s => ({ ...s, category: 'secondary' })),
        ...this.collegeStudents.map(s => ({ ...s, category: 'college' }))
      ];
    },
    
    getActiveTabCount() {
      switch(this.activeTab) {
        case 'secondary': return this.secondaryStudents.length;
        case 'college': return this.collegeStudents.length;
        default: return this.totalStudents;
      }
    },
    
    // Main statistics
    totalStudents() {
      return this.secondaryStudents.length + this.collegeStudents.length;
    },
    
    approvedStudents() {
      return this.allStudents.filter(student => student.processed === true || student.processed === 1);
    },
    
    rejectedStudents() {
      return this.allStudents.filter(student => student.processed === false || student.processed === 0);
    },
    
    pendingStudents() {
      return this.allStudents.filter(student => 
        student.processed === null || student.processed === undefined
      );
    },
    
    // Category-specific statistics
    approvedSecondaryStudents() {
      return this.secondaryStudents.filter(student => student.processed === true || student.processed === 1);
    },
    
    approvedCollegeStudents() {
      return this.collegeStudents.filter(student => student.processed === true || student.processed === 1);
    },
    
    rejectedSecondaryStudents() {
      return this.secondaryStudents.filter(student => student.processed === false || student.processed === 0);
    },
    
    rejectedCollegeStudents() {
      return this.collegeStudents.filter(student => student.processed === false || student.processed === 0);
    },
    
    pendingSecondaryStudents() {
      return this.secondaryStudents.filter(student => 
        student.processed === null || student.processed === undefined
      );
    },
    
    pendingCollegeStudents() {
      return this.collegeStudents.filter(student => 
        student.processed === null || student.processed === undefined
      );
    },
    
    // Percentages
    approvedPercentage() {
      return this.totalStudents > 0 ? Math.round((this.approvedStudents.length / this.totalStudents) * 100) : 0;
    },
    
    rejectedPercentage() {
      return this.totalStudents > 0 ? Math.round((this.rejectedStudents.length / this.totalStudents) * 100) : 0;
    },
    
    pendingPercentage() {
      return this.totalStudents > 0 ? Math.round((this.pendingStudents.length / this.totalStudents) * 100) : 0;
    },
    
    secondaryApprovalPercentage() {
      return this.secondaryStudents.length > 0 
        ? Math.round((this.approvedSecondaryStudents.length / this.secondaryStudents.length) * 100) 
        : 0;
    },
    
    collegeApprovalPercentage() {
      return this.collegeStudents.length > 0 
        ? Math.round((this.approvedCollegeStudents.length / this.collegeStudents.length) * 100) 
        : 0;
    },
    
    // Financial data
    totalAmountAwarded() {
      return this.approvedStudents.reduce((sum, student) => {
        return sum + (student.amount_awarded || 0);
      }, 0);
    },
    
    totalSecondaryAwarded() {
      return this.approvedSecondaryStudents.reduce((sum, student) => {
        return sum + (student.amount_awarded || 0);
      }, 0);
    },
    
    totalCollegeAwarded() {
      return this.approvedCollegeStudents.reduce((sum, student) => {
        return sum + (student.amount_awarded || 0);
      }, 0);
    },
    
    averageAward() {
      const awardedStudents = this.approvedStudents.filter(s => s.amount_awarded > 0);
      return awardedStudents.length > 0 
        ? Math.round(this.totalAmountAwarded / awardedStudents.length)
        : 0;
    },
    
    // Remarks analysis
    totalRemarks() {
      return this.allStudents.filter(s => s.user_remarks).length;
    },
    
    remarksApproved() {
      return this.allStudents.filter(student => 
        student.user_remarks && 
        student.user_remarks.toLowerCase().includes('student approved')
      );
    },
    
    remarksFormMissing() {
      return this.allStudents.filter(student => 
        student.user_remarks && 
        (student.user_remarks.toLowerCase().includes('form missing') || 
         student.user_remarks.toLowerCase().includes('missing form'))
      );
    },
    
    remarksVoterMissing() {
      return this.allStudents.filter(student => 
        student.user_remarks && 
        (student.user_remarks.toLowerCase().includes('voter details missing') ||
         student.user_remarks.toLowerCase().includes('missing voter'))
      );
    },
    
    // Category-specific remarks
    remarksApprovedSecondary() {
      return this.remarksApproved.filter(s => s.category === 'secondary');
    },
    
    remarksApprovedCollege() {
      return this.remarksApproved.filter(s => s.category === 'college');
    },
    
    remarksFormMissingSecondary() {
      return this.remarksFormMissing.filter(s => s.category === 'secondary');
    },
    
    remarksFormMissingCollege() {
      return this.remarksFormMissing.filter(s => s.category === 'college');
    },
    
    remarksVoterMissingSecondary() {
      return this.remarksVoterMissing.filter(s => s.category === 'secondary');
    },
    
    remarksVoterMissingCollege() {
      return this.remarksVoterMissing.filter(s => s.category === 'college');
    },
    
    // Voter verification
    voterVerifiedStudents() {
      return this.allStudents.filter(student => 
        student.voter_short_id || 
        (student.user_remarks && student.user_remarks.toLowerCase().includes('voter'))
      );
    },
    
    voterVerifiedSecondary() {
      return this.voterVerifiedStudents.filter(s => s.category === 'secondary');
    },
    
    voterVerifiedCollege() {
      return this.voterVerifiedStudents.filter(s => s.category === 'college');
    },
    
    // Ward statistics
    wardStats() {
      const wards = {};
      
      this.allStudents.forEach(student => {
        if (!student.ward) return;
        
        if (!wards[student.ward]) {
          wards[student.ward] = {
            name: student.ward,
            total: 0,
            secondary: 0,
            college: 0,
            approved: 0,
            awarded: 0
          };
        }
        
        wards[student.ward].total++;
        
        if (student.category === 'secondary') {
          wards[student.ward].secondary++;
        } else {
          wards[student.ward].college++;
        }
        
        if (student.processed === true || student.processed === 1) {
          wards[student.ward].approved++;
          wards[student.ward].awarded += (student.amount_awarded || 0);
        }
      });
      
      return Object.values(wards);
    },
    
    topWards() {
      // Create a copy before sorting to avoid side effects
      return [...this.wardStats]
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);
    }
  },
  
  methods: {
    async loadData() {
      this.loading = true;
      
      try {
        // Load secondary data
        const secondaryResponse = await fetch('https://api.roberms.com/nyamoko/get/secondary/approved');
        this.secondaryStudents = await secondaryResponse.json();
        
        // Load college data
        const collegeResponse = await fetch('https://api.roberms.com/nyamoko/get/college/approved');
        this.collegeStudents = await collegeResponse.json();
        
        this.lastUpdated = new Date().toLocaleTimeString();
      } catch (error) {
        console.error('Error loading data:', error);
        
        // Fallback to sample data
        this.secondaryStudents = [{
          "id":1,"h_id":"1","studentName":"ISMAEL ACHIKI SABOKE","idNumber":"761500616",
          "adminNumber":"6107","disability_status":"No Dissability","yob":"2008",
          "student_form":"Form 3","main_contact_number":"713368090","gender":"Male",
          "yearofadmission":"2024","sub_location":"BONYENGWE 1","mother_names":"LINET KWAMBOKA SABOKE",
          "mother_id":"26522133","mother_yob":"1989","father_id":"21148669",
          "father_names":"JULIUS SABOKE RIOBA","father_yob":"1978","guardian_name":"",
          "guardian_id":"","guardian_yob":"","schoolName":"RIOMEGO SDA SECONDARY SCHOOL",
          "school_type":"Boarding school","school_code":"427","needy_status":"Both Parents",
          "tracking_code":"form returned","amount_awarded":5000,"ward":"Magwagwa",
          "user_remarks":"Student Approved through voterJULIUS SABOKE RIOBA",
          "processed":true,"voter_short_id":"21148669","voter_dob":"1978",
          "voter_full_name":"JULIUS SABOKE RIOBA","voter_gender":"M"
        }];
        
        // Sample college data
        this.collegeStudents = [{
          "id":101,"h_id":"101","studentName":"JANE MWENDE KIMANI","idNumber":"761500617",
          "adminNumber":"C6108","disability_status":"No Dissability","yob":"2000",
          "student_form":"Year 3","main_contact_number":"712345678","gender":"Female",
          "yearofadmission":"2022","sub_location":"TOWN CENTER","mother_names":"MARY KIMANI",
          "mother_id":"26522134","mother_yob":"1975","father_id":"21148670",
          "father_names":"JOHN KIMANI","father_yob":"1970","guardian_name":"",
          "guardian_id":"","guardian_yob":"","schoolName":"UNIVERSITY OF NAIROBI",
          "school_type":"Public University","school_code":"UON001","needy_status":"Orphan",
          "tracking_code":"approved","amount_awarded":15000,"ward":"Itibo",
          "user_remarks":"Student approved but form missing",
          "processed":true,"voter_short_id":"","voter_dob":"",
          "voter_full_name":"","voter_gender":""
        }];
      } finally {
        this.loading = false;
      }
    },
    
    refreshData() {
      this.loadData();
    },
    
    viewVoterVerified() {
      this.$router.push('/students/voter-verified');
    }
  },
  
  created() {
    this.loadData();
    
    // Refresh data every 5 minutes
    this.refreshInterval = setInterval(() => {
      this.refreshData();
    }, 300000);
  },
  
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  
  mounted() {
    // Load Bootstrap Icons if not already loaded
    if (!document.querySelector('link[href*="bootstrap-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.8.1/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }
  }
};
</script>

<style scoped>
.admin-dashboard {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-card {
  transition: transform 0.2s ease-in-out;
  border-radius: 12px;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress {
  background-color: #e9ecef;
}

.action-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-group-item {
  border-radius: 8px !important;
  margin-bottom: 8px;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

/* Custom colors */
.bg-purple {
  background-color: #6f42c1 !important;
}

.text-purple {
  color: #6f42c1 !important;
}

.btn-outline-purple {
  color: #6f42c1;
  border-color: #6f42c1;
}

.btn-outline-purple:hover {
  background-color: #6f42c1;
  color: white;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .display-6 {
    font-size: 2rem;
  }
  
  .dashboard-header h1 {
    font-size: 1.5rem;
  }
  
  .btn-group .btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
  }
}

/* Animation for cards */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeIn 0.5s ease-out;
}

/* Loading state */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Category badge styling */
.badge.bg-primary {
  background-color: #007bff !important;
}

.badge.bg-success {
  background-color: #28a745 !important;
}

/* Border utilities */
.border-success {
  border-color: #28a745 !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.border-danger {
  border-color: #dc3545 !important;
}

/* Hover effects */
.hover-lift {
  transition: all 0.3s ease;
}

.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}
</style>